/**
 * The lazy game-graph seam (lazy-game-graph-boot-seam design). `ensureGameUi()` memoizes the
 * dynamic import of game-boot.js — the lazy root that statically pulls in game.js, the animation
 * controller/dispatch, the bid pipeline, and board-layout.js — and runs its one-time wiring
 * (`initGameGraph`) on first resolution. `getGameUi()` is the sync-when-warm read the eager seam
 * adapters (boot.js) use once the graph may already be resolved, without forcing an await: the
 * perf-lazy-result-surface shape (game.js's `resultUi`/`resultUiPromise`), one level up.
 */
import { LAZY_MODULE_URLS } from "./generated/lazy-modules.js";
import { scheduleIdle } from "./utils.js";
import { checkStaleClient } from "./stale-client.js";

let gameUi = null;
let gameUiPromise = null;

/** Memoized loader — safe to call repeatedly (the game-start idle warmup below never calls this;
 *  Play, the onAfterApi seam adapter, and any dev-flag force-ensure do). Resets the memo on
 *  rejection (perf-lazy-result-surface precedent) so a transient network blip doesn't permanently
 *  strand the game behind a poisoned promise — the next call, whichever caller makes it, retries. */
export function ensureGameUi() {
  if (gameUiPromise == null) {
    gameUiPromise = import("./game-boot.js").then(
      async (mod) => {
        await mod.initGameGraph();
        gameUi = mod;
        return mod;
      },
      (err) => {
        gameUiPromise = null;
        // A retired hashed chunk 404s permanently, so the retry above can never succeed — probe
        // the entry URL and reload if a deploy is what broke this import (stale-client.js).
        checkStaleClient();
        throw err;
      },
    );
  }
  return gameUiPromise;
}

/** The resolved graph handle, or null before ensureGameUi() has resolved once — the sync-when-warm
 *  read the seam adapters (boot.js) use so a warm graph never pays a microtask yield it doesn't
 *  need (onBeforeApi is a snapshot taken synchronously before the request fires). */
export function getGameUi() {
  return gameUi;
}

let warmed = false;

// why: batches the 115-link injection (tbt-reshaping slice 1 §4) so each idle
// callback's DOM-append work stays a short task instead of one ~350ms one (local
// 4× — see the tbt-entrance-probe.mjs numbers in the design's Current state).
const WARM_BATCH_SIZE = 20;

/** One-shot fetch-only preload of the lazy graph's bytes — `<link rel="modulepreload">` per URL in
 *  the generated lazy-modules list, injected in batches across idle callbacks (game-start idle
 *  window) instead of one blocking loop. The one-shot latch is claimed HERE, at entry, so a second
 *  call issued while batches are still in flight is a no-op rather than double-injecting. Preload
 *  only: it never imports/evaluates a module, so it can't pay the eval cost ensureGameUi() defers
 *  to Play — see the design's "Warmup (fetch-only)".
 *
 *  In the prod bundle the list is a different one: build-frontend.js's stub replaces the generated
 *  import with a read of the chunk URLs game.html carries as a JSON island, so the same loop warms
 *  the build's lazy chunks — one batch, not six (manifest-chunk-preloads design § 1). */
export function warmGameGraph() {
  if (warmed) return;
  warmed = true;
  injectLinkBatch(0);
}

function injectLinkBatch(start) {
  const end = Math.min(start + WARM_BATCH_SIZE, LAZY_MODULE_URLS.length);
  for (let i = start; i < end; i++) {
    const link = document.createElement("link");
    link.rel = "modulepreload";
    link.href = LAZY_MODULE_URLS[i];
    document.head.appendChild(link);
  }
  if (end < LAZY_MODULE_URLS.length) scheduleIdle(() => injectLinkBatch(end));
}
